import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { normalizePhone } from "@/lib/phone";

/**
 * Pre-reservation flow for sold-out PDP.
 * Free reservation. Phone verified via existing WhatsApp OTP pipeline
 * (Interakt signup_otp template). All writes use service_role.
 */

const PhoneSchema = z
  .string()
  .min(8)
  .max(20)
  .regex(/^[+0-9 \-()]+$/, "invalid phone");

// ---- requestReservationOtp -------------------------------------------
export const requestReservationOtp = createServerFn({ method: "POST" })
  .inputValidator((data: { phone: string }) =>
    z.object({ phone: PhoneSchema }).parse(data),
  )
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    if (!phone || phone.length < 12) {
      return { ok: false as const, error: "invalid_phone" as const };
    }
    const { requestSignupOtp } = await import("@/lib/whatsapp/otp.server");
    const result = await requestSignupOtp({ phone, purpose: "signup" });
    if (!result.ok) {
      return { ok: false as const, error: result.error, retryAfterSec: result.retryAfterSec };
    }
    return { ok: true as const, cooldownSeconds: result.cooldownSeconds, phone };
  });

// ---- verifyReservationOtp --------------------------------------------
export const verifyReservationOtp = createServerFn({ method: "POST" })
  .inputValidator((data: { phone: string; code: string; anonymousId?: string | null }) =>
    z
      .object({
        phone: PhoneSchema,
        code: z.string().regex(/^\d{6}$/, "6-digit code"),
        anonymousId: z.string().min(8).max(64).optional().nullable(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    if (!phone) return { ok: false as const, error: "invalid" as const };
    const { verifyOtp } = await import("@/lib/whatsapp/otp.server");
    const result = await verifyOtp({ phone, code: data.code, purpose: "signup" });
    if (!result.ok) return { ok: false as const, error: result.error };

    // Bind this successful verification to this caller. The save step accepts
    // only this opaque proof — never "some OTP for this number was consumed".
    const { mintVerificationProof, currentRequesterFingerprint } = await import(
      "@/lib/reservations/verification.server"
    );
    const proof = await mintVerificationProof({
      phoneE164: phone,
      fingerprint: await currentRequesterFingerprint(),
      anonymousId: data.anonymousId ?? null,
    });
    if (!proof) return { ok: false as const, error: "invalid" as const };

    // Real hand-raise breadcrumb: this visitor just verified their number.
    // Best-effort; never blocks the verification response.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      if (data.anonymousId) {
        await supabaseAdmin.from("lead_events").insert({
          anonymous_id: data.anonymousId,
          event_type: "signup_verified",
          properties: { purpose: "signup" } as never,
        } as never);
      }
      // Make the hand-raise visible in Admin → Leads as an identified contact.
      const { identifyLeadFromReserve } = await import("@/lib/leads/reserve-identify.server");
      await identifyLeadFromReserve(supabaseAdmin, {
        phoneE164: phone,
        anonymousId: data.anonymousId ?? null,
        verifiedAt: new Date().toISOString(),
      });
    } catch {
      // ignore
    }
    return { ok: true as const, phone, verification: proof };
  });

// ---- createProductReservation ----------------------------------------
const AddressSchema = z.object({
  line1: z.string().trim().min(3).max(200),
  line2: z.string().trim().max(200).optional().nullable(),
  city: z.string().trim().min(2).max(80),
  state: z.string().trim().min(2).max(80),
  pincode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "6-digit pincode"),
});

export const createProductReservation = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      productHandle: string;
      name: string;
      phone: string;
      email?: string | null;
      address: {
        line1: string;
        line2?: string | null;
        city: string;
        state: string;
        pincode: string;
      };
      reason?: string | null;
      anonymousId?: string | null;
      source?: string | null;
      internal?: boolean | null;
      promotionalChannels?: Partial<Record<"whatsapp" | "email" | "sms" | "phone" | "push", boolean>>;
      /** Opaque proof minted when THIS caller verified THIS phone by OTP. */
      verification?: string | null;
    }) =>
      z
        .object({
          productHandle: z.string().min(1).max(80),
          name: z.string().trim().min(1).max(120),
          phone: PhoneSchema,
          email: z.string().email().max(255).optional().nullable(),
          address: AddressSchema,
          reason: z
            .string()
            .trim()
            .max(500, "Reason must be 500 characters or less")
            .refine((s) => !/[\x00-\x08\x0B-\x1F\x7F]/.test(s), "Invalid characters in reason")
            .optional()
            .nullable(),
          anonymousId: z.string().min(8).max(64).optional().nullable(),
          source: z.string().max(64).optional().nullable(),
          /** Client-observed internal/automated traffic marker. */
          internal: z.boolean().optional().nullable(),
          promotionalChannels: z.object({
            whatsapp: z.boolean().optional(), email: z.boolean().optional(),
            sms: z.boolean().optional(), phone: z.boolean().optional(), push: z.boolean().optional(),
          }).optional(),
          verification: z.string().min(20).max(200).optional().nullable(),
        })
        .parse(data),
  )
  .handler(async ({ data }) => {
    const phone = normalizePhone(data.phone);
    if (!phone) {
      return { ok: false as const, error: "invalid_phone" as const };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Proof of verification must belong to THIS caller and THIS phone.
    // "An OTP for this number was consumed recently" is not proof — anyone who
    // knows the number could ride on someone else's verification.
    const { consumeVerificationProof, currentRequesterFingerprint } = await import(
      "@/lib/reservations/verification.server"
    );
    const proof = await consumeVerificationProof({
      token: data.verification ?? null,
      phoneE164: phone,
      fingerprint: await currentRequesterFingerprint(),
    });
    if (!proof) {
      return { ok: false as const, error: "unverified" as const };
    }

    // Identity for consent comes from the verified request itself, never from
    // an arbitrary posted value.
    const verifiedAnonymousId = proof.anonymousId;

    // Identity and eligibility are resolved BEFORE the save, because the save
    // and the conversion record are written in one transaction.
    let customerId: string | null = proof.customerId;
    let linkedEmail: string | null = null;
    try {
      // Securely linked identity only: the customer record for the very phone
      // number just verified by OTP. Never an unrelated lookup.
      const { data: cust } = await supabaseAdmin
        .from("customers")
        .select("id, email")
        .eq("phone", phone)
        .maybeSingle();
      customerId = cust?.id ?? customerId;
      linkedEmail = cust?.email ?? null;
    } catch {
      linkedEmail = null;
    }

    // Origin comes from the framework-parsed request URL, not from arbitrary
    // forwarded headers a caller could set.
    let originHost: string | null = null;
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const req = getRequest();
      originHost = req ? new URL(req.url).hostname.toLowerCase() : null;
    } catch {
      originHost = null;
    }

    // Advertising permission frozen at the moment of completion. A later
    // opt-in must never retroactively authorise this event.
    let consentAtCompletion: "granted" | "denied" | "unknown" = "unknown";
    try {
      const { getConsentStatus } = await import("@/lib/consent/state.server");
      consentAtCompletion = (await getConsentStatus("advertising", {
        // Identity bound to the verified request, not to a posted value.
        anonymousId: verifiedAnonymousId,
        customerId,
      })) as "granted" | "denied" | "unknown";
    } catch {
      consentAtCompletion = "unknown";
    }

    const { savePrebookingReservation } = await import("@/lib/meta/prebooking.server");
    const saved = await savePrebookingReservation({
      productHandle: data.productHandle,
      name: data.name.trim(),
      phoneE164: phone,
      // Submitted email wins; otherwise the securely linked customer email.
      email: (data.email ? data.email.trim().toLowerCase() : null) ?? linkedEmail,
      addressLine1: data.address.line1.trim(),
      addressLine2: data.address.line2?.trim() || null,
      city: data.address.city.trim(),
      state: data.address.state.trim(),
      pincode: data.address.pincode.trim(),
      reason: data.reason?.trim() || null,
      anonymousId: verifiedAnonymousId,
      customerId,
      source: data.source || "pdp-sold-out",
      originHost,
      consentAtCompletion,
      internalTraffic: data.internal === true,
    });

    if (!saved) {
      console.error("[reservations] atomic save failed");
      return { ok: false as const, error: "save_failed" as const };
    }
    const inserted = { id: saved.reservationId };

    // Retargeting breadcrumb (best-effort; ignore failures).
    if (verifiedAnonymousId) {
      try {
        await supabaseAdmin.from("lead_events").insert({
          anonymous_id: verifiedAnonymousId,
          event_type: "product_reserved",
          properties: {
            handle: data.productHandle,
            reservation_id: inserted?.id,
          } as never,
          route: `/products/${data.productHandle}`,
        });
      } catch (e) {
        console.warn("[reservations] lead_events insert failed", (e as Error).message);
      }
    }

    // A completed pre-reservation is an identified, warm contact in Leads.
    try {
      const { identifyLeadFromReserve } = await import("@/lib/leads/reserve-identify.server");
      await identifyLeadFromReserve(supabaseAdmin, {
        phoneE164: phone,
        anonymousId: verifiedAnonymousId ?? null,
        email: (data.email ? data.email.trim().toLowerCase() : null) ?? linkedEmail,
        customerId,
        productHandle: data.productHandle,
        verifiedAt: new Date().toISOString(),
      });
    } catch (e) {
      console.warn("[reservations] lead identify failed", (e as Error).message);
    }

    // OTP/reservation consent permits service messages, not general offers.
    // An unchecked box makes no change to an existing promotional preference.
    if (data.promotionalChannels) {
      try {
        const { writeConsent } = await import("@/lib/consent/state.server");
        const { COMPANY_MARKETING_NOTICE_VERSION, COMPANY_MARKETING_CHANNELS } = await import("@/lib/consent/company-marketing");
        // Verification above binds this request to this phone and identity.
        // Write even if the channel was already granted: the expanded purpose
        // needs a new, explicit, versioned evidence record.
        for (const channel of COMPANY_MARKETING_CHANNELS) {
          if (data.promotionalChannels[channel] !== true) continue;
          if (channel === "email" && !data.email) continue;
          const result = await writeConsent({
            channel, status: "granted", source: "prereserve_omkara_brands",
            policyVersion: COMPANY_MARKETING_NOTICE_VERSION,
            identity: { anonymousId: verifiedAnonymousId, customerId }, explicit: true,
          });
          if (!result.ok) console.warn("[reservations] company marketing permission not saved", channel);
        }
      } catch (e) {
        console.warn("[reservations] promotional opt-in record failed", (e as Error).name);
      }
    }

    // Only a first completion has a freshly created conversion record (written
    // in the same transaction). Re-saving an existing reservation never
    // enqueues, so there is nothing new to deliver.
    if (saved.created && saved.eventId) {
      try {
        const { drainPrebookingOutbox } = await import("@/lib/meta/prebooking.server");
        await drainPrebookingOutbox({ limit: 1, eventId: saved.eventId });
      } catch (e) {
        console.warn("[reservations] prebooking delivery skipped", (e as Error).name);
      }
    }

    // Thank-you confirmation on WhatsApp. Only on a first completion, and only
    // through the same gate every other template uses (suppression, consent,
    // dedupe). Never blocks the reservation response.
    if (saved.created) {
      try {
        const { sendPrereserveConfirmation } = await import("@/lib/whatsapp/templates.server");
        const { titleForHandle } = await import("@/lib/reservations/product-titles");
        await sendPrereserveConfirmation(phone, {
          firstName: data.name.trim().split(/\s+/)[0],
          productTitle: titleForHandle(data.productHandle),
          productHandle: data.productHandle,
          customerId,
          reservationId: inserted?.id ?? null,
        });
      } catch (e) {
        console.warn("[reservations] confirmation send skipped", (e as Error).message);
      }
    }

    return { ok: true as const, reservationId: inserted?.id };

  });
